import { Base, withBreakpointObserver } from '@studiometa/js-toolkit';
import Swiper from 'swiper';
import { Navigation, Pagination } from 'swiper/modules';

/**
 * Slider component
 */
export default class Slider extends withBreakpointObserver(Base) {
  /**
   * Component Configuration
   */
  static config = {
    name: 'Slider',
    refs: ['slider', 'prev', 'next', 'pagination', 'caption'],
    options: {
      pagination: {
        type: String,
        default: 'bullets',
      },
      spaceBetween: {
        type: Number,
        default: 0,
      },
      slidesPerView: {
        type: Number,
        default: 0,
      },
      align: {
        type: String,
        default: '',
      },
    },
  };

  /**
   * Calculate offset to align last slide to start
   * @returns {number}
   */
  getSlidesOffsetAfter() {
    if (this.$options.align !== 'start') return 0;
    const slides = [...this.$refs.slider.querySelectorAll('.swiper-slide')];
    const lastSlide = slides.at(-1);
    if (!lastSlide) return 0;
    return this.$refs.slider.offsetWidth - lastSlide.offsetWidth;
  }

  /**
   * Initialize Swiper instance and caption tracking
   */
  mounted() {
    this.slider = new Swiper(this.$refs.slider, {
      modules: [Navigation, Pagination],
      slidesPerView: 'auto',
      grabCursor: true,
      slideToClickedSlide: true,
      spaceBetween: this.$options.spaceBetween,
      slidesOffsetAfter: this.getSlidesOffsetAfter(),
      navigation: {
        nextEl: this.$refs.next,
        prevEl: this.$refs.prev,
      },
      pagination: {
        clickable: true,
        el: this.$refs.pagination,
        type: this.$options.pagination,
      },
      breakpoints: {
        480: {
          slidesPerView: this.$options.slidesPerView > 2 ? this.$options.slidesPerView : 'auto',
        },
        640: {
          slidesPerView: this.$options.slidesPerView > 3 ? this.$options.slidesPerView : 'auto',
        },
        1024: {
          slidesPerView: this.$options.slidesPerView > 0 ? this.$options.slidesPerView : 'auto',
        },
      },
    });

    if (this.$refs.caption) {
      this.captions = [...this.$refs.slider.querySelectorAll('.swiper-slide')].map(
        (slide) => slide.dataset.caption ?? '',
      );
      this.$refs.caption.textContent = this.captions[0] ?? '';
      this.slider.on('slideChange', () => this.updateCaption());
    }
  }

  /**
   * Update caption text from active slide with fade transition
   */
  updateCaption() {
    const caption = this.$refs.caption;
    const text = this.captions[this.slider.realIndex] ?? '';

    if (caption.textContent === text) return;

    const update = () => {
      caption.textContent = text;
      caption.style.opacity = text ? '1' : '0';
    };

    if (getComputedStyle(caption).opacity === '0') {
      update();
      return;
    }

    caption.style.opacity = '0';
    caption.addEventListener('transitionend', update, { once: true });
  }

  /**
   * Détruit l'instance Swiper au démontage.
   *
   * Le composant peut être démonté puis remonté par `withBreakpointObserver`
   * (via `data-option-active-breakpoints`) : sans ça, chaque remontage empile
   * une nouvelle instance Swiper sur le même élément.
   */
  destroyed() {
    this.slider?.destroy(true, true);
    this.slider = null;

    // Swiper pose `disabled` sur les <button> de navigation quand on atteint une
    // extrémité, mais son `destroy` ne retire que les classes : l'attribut reste.
    // Sans ce nettoyage, une flèche désactivée le reste après un démontage.
    [this.$refs.prev, this.$refs.next].forEach((button) => {
      if (button) {
        button.disabled = false;
        button.classList.remove('swiper-button-lock');
      }
    });
  }

  /**
   * Recalculate offset on resize
   */
  resized() {
    if (this.$options.align === 'start' && this.slider) {
      this.slider.params.slidesOffsetAfter = this.getSlidesOffsetAfter();
      this.slider.update();
    }
  }
}
